import React, { useState, useRef } from "react";
import { Play } from "lucide-react";

const GOLD = "#c9a227";

/**
 * Click-to-play vertical session video.
 * Shows a still poster + gold play button until the user clicks.
 * Saves bandwidth (video isn't loaded until interaction).
 */
export const InsideSessionVideo = ({
    src = "/brochure-assets/essam.mp4",
    poster, // optional still frame
    testId = "inside-session-video",
}) => {
    const [playing, setPlaying] = useState(false);
    const videoRef = useRef(null);

    const handlePlay = () => {
        setPlaying(true);
        // Defer to next tick so the <video> mounts before we call .play()
        setTimeout(() => {
            if (videoRef.current) {
                videoRef.current
                    .play()
                    .catch(() => {
                        /* user can hit native controls */
                    });
            }
        }, 50);
    };

    return (
        <div
            data-testid={testId}
            style={{
                position: "relative",
                width: "100%",
                maxWidth: 380,
                margin: "0 auto",
                aspectRatio: "9 / 16",
                backgroundColor: "#000",
                border: `1px solid rgba(201,162,39,0.3)`,
                overflow: "hidden",
                boxShadow:
                    "0 30px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(201,162,39,0.06)",
            }}
        >
            {!playing ? (
                <button
                    type="button"
                    onClick={handlePlay}
                    data-testid={`${testId}-play-btn`}
                    aria-label="Play session video"
                    style={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "transparent",
                        backgroundImage: poster ? `url(${poster})` : "none",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        border: "none",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 0,
                    }}
                >
                    {/* subtle dark overlay so play button always reads */}
                    <span
                        aria-hidden="true"
                        style={{
                            position: "absolute",
                            inset: 0,
                            background:
                                "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.55) 100%)",
                        }}
                    />
                    {/* gold ring + play icon */}
                    <span
                        aria-hidden="true"
                        style={{
                            position: "relative",
                            width: 84,
                            height: 84,
                            borderRadius: "50%",
                            border: `1.5px solid ${GOLD}`,
                            backgroundColor: "rgba(10,10,10,0.65)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "0 18px 60px rgba(201,162,39,0.3)",
                        }}
                    >
                        <Play
                            size={32}
                            color={GOLD}
                            fill={GOLD}
                            strokeWidth={0}
                            style={{ marginLeft: 4 }}
                        />
                    </span>
                    <span
                        style={{
                            position: "absolute",
                            bottom: 20,
                            left: 0,
                            right: 0,
                            textAlign: "center",
                            color: GOLD,
                            fontSize: 10,
                            letterSpacing: "0.4em",
                            textTransform: "uppercase",
                            fontWeight: 600,
                            fontFamily: "Inter, sans-serif",
                        }}
                    >
                        Watch
                    </span>
                </button>
            ) : (
                <video
                    ref={videoRef}
                    data-testid={`${testId}-player`}
                    controls
                    playsInline
                    preload="metadata"
                    poster={poster}
                    style={{
                        width: "100%",
                        height: "100%",
                        display: "block",
                        backgroundColor: "#000",
                        objectFit: "cover",
                    }}
                >
                    <source src={src} type="video/mp4" />
                </video>
            )}
        </div>
    );
};

export default InsideSessionVideo;
